import React, { useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import DOMPurify from 'dompurify';

// Paleta acotada a propósito — "básico" pedido explícitamente por el
// usuario, no un selector de color libre. Los 3 del medio tienen sentido
// clínico (alerta/atención, resaltado positivo); el gris es una
// alternativa al negro para texto secundario. "Por defecto" es negro
// explícito (#000000), no "sin color" — execCommand('foreColor', ...,
// 'inherit') no funciona de forma confiable entre navegadores para
// "quitar" el color, así que en vez de tratar de volver a un estado sin
// estilo, se aplica directamente el color negro como cualquier otro.
const TEXT_COLORS = [
  { label: 'Por defecto (negro)', value: '#000000' },
  { label: 'Rojo (alerta)', value: '#c62828' },
  { label: 'Verde', value: '#2e7d32' },
  { label: 'Azul', value: '#1565c0' },
  { label: 'Gris', value: '#616161' },
];

// Whitelist de lo que puede sobrevivir en el HTML guardado — se aplica
// tanto al guardar (acá) como al mostrar (RichTextView), nunca se confía
// en un solo punto de sanitización. Nada de scripts, iframes, atributos
// on*, ni estilos fuera de "color" —ésto es notas de HC, no un editor de
// propósito general.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'span', 'br', 'div', 'p'],
  ALLOWED_ATTR: ['style'],
  ALLOWED_STYLES: undefined, // DOMPurify no filtra por propiedad CSS individual; se filtra aparte, ver sanitizeNotesHtml
};

// DOMPurify permite el atributo "style" completo si está en ALLOWED_ATTR —
// no filtra por propiedad CSS. Para no dejar pasar cosas como
// "position:fixed" o "background:url(javascript:...)" escondidas en un
// pegado de HTML externo, se vuelve a recorrer el DOM ya sanitizado y se
// reduce cada style a, como mucho, un color de texto en la paleta permitida.
export function sanitizeNotesHtml(html) {
  if (!html) return '';
  // document.execCommand('foreColor', ...) genera <font color="#hex">, NO
  // <span style="color:...">, sin importar qué se le pida al comando — es
  // el comportamiento real del navegador, confirmado con un dump del HTML
  // en cada paso. <font> no está en ALLOWED_TAGS (es una tag obsoleta,
  // sin sentido sumarla solo por esto) — se normaliza a <span> ANTES de
  // sanitizar, si no DOMPurify la elimina entera y el color se pierde en
  // silencio (el bug real reportado: el texto quedaba sin color al
  // guardar, aunque se viera bien mientras se editaba).
  const normalized = html.replace(
    /<font\s+color=(["'])(#[0-9a-fA-F]{6})\1[^>]*>/gi,
    (_, _q, hex) => `<span style="color: ${hex}">`
  ).replace(/<\/font>/gi, '</span>');
  const clean = DOMPurify.sanitize(normalized, SANITIZE_CONFIG);
  const container = document.createElement('div');
  container.innerHTML = clean;
  const allowedColors = new Set(TEXT_COLORS.map(c => c.value).filter(Boolean));
  container.querySelectorAll('[style]').forEach(el => {
    const color = el.style.color;
    el.removeAttribute('style');
    if (color) {
      const hex = rgbToHexIfKnown(color);
      if (hex && allowedColors.has(hex)) el.style.color = hex;
    }
  });
  return container.innerHTML;
}

// El navegador normaliza cualquier color seteado por JS a rgb(...) al
// leerlo de vuelta desde el DOM — se compara contra la paleta convirtiendo
// cada hex conocido a su forma rgb() en vez de parsear rgb a mano.
function rgbToHexIfKnown(rgbOrHex) {
  if (rgbOrHex.startsWith('#')) return rgbOrHex;
  const probe = document.createElement('div');
  for (const { value: hex } of TEXT_COLORS) {
    if (!hex) continue;
    probe.style.color = hex;
    if (probe.style.color === rgbOrHex) return hex;
  }
  return null;
}

// Editor "básico" pedido explícitamente: negrita, cursiva, un puñado de
// colores de texto, quitar formato. Sin librería de rich text (Quill/
// TipTap) a propósito — contentEditable + document.execCommand alcanza
// para este alcance y no suma una dependencia pesada con su propio CSS a
// integrar con el tema. El HTML se sanitiza siempre al guardar
// (MedicalRecordPanel llama sanitizeNotesHtml antes de mandar al backend).
const RichTextEditor = ({ value, onChange, placeholder, autoFocus, minRows = 8 }) => {
  const editorRef = useRef(null);

  // Solo se pisa el contenido del DOM cuando `value` cambia por una razón
  // EXTERNA (ej. se cargó otra consulta o se aplicó una plantilla) — el
  // caso normal mientras el usuario tipea es que `value` ya coincide con
  // el innerHTML actual (onInput ya lo sincronizó vía emitChange), así que
  // el `if` de abajo no hace nada y el cursor no se mueve. Si lo
  // pisáramos siempre, el cursor saltaría al principio en cada letra
  // tipeada (contentEditable no es un input controlado normal).
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      // Plantillas/HC guardadas antes del editor de texto enriquecido (o
      // cualquier texto que llegue como texto plano con \n, sin ningún
      // <div>/<p>/<br>) se asignaban tal cual a innerHTML — el navegador
      // colapsa esos \n sueltos como espacio en blanco HTML normal,
      // dejando todo el texto junto. Se convierten a <br> solo cuando no
      // hay ningún tag de bloque/salto ya presente, para no duplicar
      // saltos en contenido que ya es HTML real.
      const raw = value || '';
      const hasBlockMarkup = /<(div|p|br)\b/i.test(raw);
      editorRef.current.innerHTML = hasBlockMarkup ? raw : raw.replace(/\n/g, '<br>');
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = (command, arg) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  const applyColor = (hex) => {
    editorRef.current?.focus();
    document.execCommand('foreColor', false, hex);
    emitChange();
  };

  const clearFormat = () => exec('removeFormat');

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.25, p: 0.5,
        borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap',
      }}>
        <Tooltip title="Negrita">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}>
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cursiva">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}>
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
        {TEXT_COLORS.map(({ label, value: hex }) => (
          <Tooltip key={label} title={label}>
            <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => applyColor(hex)}>
              <FormatColorTextIcon fontSize="small" sx={{ color: hex || 'text.primary' }} />
            </IconButton>
          </Tooltip>
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
        <Tooltip title="Quitar formato">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={clearFormat}>
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        autoFocus={autoFocus}
        onInput={emitChange}
        onPaste={(e) => {
          // Pegado siempre como texto plano — pegar HTML de otra página
          // (ej. copiar de Word/un mail) es la vía más común para meter
          // markup no deseado; se sanitiza igual al guardar, pero esto
          // evita arrastrar estilos/etiquetas raras desde el portapapeles.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        data-placeholder={placeholder}
        sx={{
          minHeight: `${minRows * 1.6}em`,
          p: 1.5,
          fontSize: '0.95rem',
          fontFamily: 'inherit',
          outline: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
          },
        }}
      />
    </Box>
  );
};

// Vista de solo lectura del mismo HTML — re-sanitiza siempre (defensa en
// profundidad: aunque ya se sanitizó al guardar, un registro viejo o un
// dato tocado directo en la base no debe poder inyectar nada al mostrarse).
//
// Caso real reportado: médicos que casi no usan plantillas escriben la
// evolución como una línea por dato ("G1", "AP: obesidad", "MH: no"...),
// que el contentEditable del editor ya guarda como un <div>/<p> por
// línea — pero antes se mostraba con el line-height/spacing por default
// del navegador, compactado y sin ninguna jerarquía visual entre líneas,
// difícil de escanear de un vistazo. No se inventa ninguna detección de
// texto (sería frágil: "dolor: intenso" no es una etiqueta real) — se le
// da más aire a la estructura por líneas que el médico YA está creando al
// escribir, sin que tenga que cambiar cómo tipea.
export const RichTextView = ({ html, sx }) => (
  <Box
    sx={{
      whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.875rem', lineHeight: 1.7,
      '& p, & div': { mb: '4px' },
      '& p:last-child, & div:last-child': { mb: 0 },
      ...sx,
    }}
    dangerouslySetInnerHTML={{ __html: sanitizeNotesHtml(html) }}
  />
);

export default RichTextEditor;
